/**
 * Form and status primitives shared by the admin panels and the
 * member-facing forms. They started under admin/, but the sign-up and
 * rating forms need exactly the same label, hint and error treatment —
 * duplicating it there let the two drift apart visually.
 */

import type { ReactNode } from "react";

interface FieldProps {
  readonly label: string;
  readonly htmlFor: string;
  readonly hint?: string;
  readonly error?: string;
  readonly children: ReactNode;
  readonly className?: string;
}

export function Field({ label, htmlFor, hint, error, children, className }: FieldProps) {
  return (
    <div className={className}>
      <label className="label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {error ? (
        <p className="mt-1 text-[0.8125rem] font-semibold text-piece-z">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-[0.8125rem] leading-snug opacity-60">{hint}</p>
      ) : null}
    </div>
  );
}

export type BannerTone = "ok" | "error" | "info";

const TONE_STYLE: Record<BannerTone, string> = {
  ok: "bg-piece-s/25",
  error: "bg-piece-z/20",
  info: "bg-paper",
};

/** Save results and failures share one place on screen, so nothing is missed. */
export function StatusBanner({
  tone,
  message,
  prefix = "Could not save — ",
}: {
  readonly tone: BannerTone;
  readonly message: string | null;
  /**
   * Lead-in shown before an error. Defaults to the save wording the admin
   * panels want; pass `null` on a form that is not saving anything, where
   * "Could not save — that username is already taken" reads as nonsense.
   */
  readonly prefix?: string | null;
}) {
  if (!message) return null;
  return (
    <p
      role={tone === "error" ? "alert" : "status"}
      className={`border-2 border-ink px-4 py-2.5 text-[0.9375rem] leading-snug ${TONE_STYLE[tone]}`}
    >
      {tone === "error" && prefix ? <span className="font-bold">{prefix}</span> : null}
      {message}
    </p>
  );
}

export function PanelSection({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}) {
  return (
    <section className="panel p-5 sm:p-6">
      <h2 className="text-[1.375rem]">{title}</h2>
      {description ? (
        <p className="mt-1.5 max-w-[62ch] text-[0.9375rem] leading-relaxed opacity-70">
          {description}
        </p>
      ) : null}
      <div className="mt-5">{children}</div>
    </section>
  );
}

/** A right-aligned action row that keeps the primary button in a fixed place. */
export function ActionRow({ children }: { readonly children: ReactNode }) {
  return (
    <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t-2 border-ink pt-5">
      {children}
    </div>
  );
}
